import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaClock, FaCalendar, FaSyncAlt } from 'react-icons/fa'
import { Heading, Text, Icon, Box, Stack, SimpleTable, SimpleGrid, Select, Tooltip, EmptyBox, Badge, Card, SimpleTabs, Loading, Button } from '@/components/lib'

import api from '../../../api'
import utils from '@/utils/utils'
import useStore from '@/store'
import StageScheduleOverrideModal from '@/components/includes/StageScheduleOverrideModal'
import StageScheduleResetPopover from '@/components/includes/StageScheduleResetPopover'

const ParticipantTimelineCalendar = lazy(() => import('./ParticipantTimelineCalendar'))

export default function Timeline ({ trialId, participantId, participant }) {
  const { user, trial } = useStore()
  const [stages, setStages] = useState([])
  const [ratioComplete, setRatioComplete] = useState(0)
  const [filter, setFilter] = useState('all')
  const [timelineView, setTimelineView] = useState('timeline')
  const [scheduleOverrideStage, setScheduleOverrideStage] = useState(null)
  const [resetOverrideStage, setResetOverrideStage] = useState(null)
  const canRescheduleStages = utils.canWriteTrial(user, trial)

  const refreshStages = useCallback(() => {
    api.participants.listStages(trialId, participantId, (d) => {
      setStages(d.stages)
      setRatioComplete(d.ratioComplete)
    })
  }, [trialId, participantId])

  useEffect(() => {
    refreshStages()
  }, [refreshStages])

  const adHocs = stages.filter(s => s.adHoc)
  const timepoints = stages.filter(s => !s.adHoc)
  let listFilteredTimepoints = timepoints
  if (filter === 'complete') listFilteredTimepoints = timepoints.filter(s => s.completed)
  if (filter === 'incomplete') listFilteredTimepoints = timepoints.filter(s => !s.completed)

  const sortedTimepoints = listFilteredTimepoints.filter(s => s.enableAt).sort((a, b) => a.enableAt - b.enableAt).concat(listFilteredTimepoints.filter(s => !s.enableAt))

  return (
    <Stack gap={5}>
      {stages.length === 0 && <EmptyBox title='No stages are currently available for this participant.' />}

      {stages.length > 0 &&
        <>
          {adHocs.length > 0 &&
            <Stack gap={3}>
              <Heading size='md'>Ad-hoc stages</Heading>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={5}>
                {adHocs.map((stage, index) => (
                  <Card key={index}>
                    <Text fontSize='md'>{stage.name}</Text>
                    {stage.lastRecordedAt && <Text fontSize='sm' color='fg.subtle'>Last logged: {utils.formatDateTime(stage.lastRecordedAt)}</Text>}
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>}

          {timepoints.length > 0 &&
            <Stack gap={3}>
              <Heading size='md'>Timepoints {ratioComplete ? <small>({Math.ceil(ratioComplete * 100)}% complete)</small> : ''}</Heading>
              <SimpleTabs
                wrapped
                lazyMount
                value={timelineView}
                onValueChange={setTimelineView}
                tabs={[
                  {
                    key: 'timeline',
                    title: 'Timeline',
                    content: (
                      <Stack gap={3}>
                        <Select size='sm' value={filter} onChange={e => setFilter(e.target.value)} w='fit-content' maxW='100%' variant='filled'>
                          <option value='all'>View all</option>
                          <option value='complete'>View completed stages</option>
                          <option value='incomplete'>View incomplete stages</option>
                        </Select>
                        <SimpleTable
                          size='sm'
                          headers={['', 'Stage', 'Recurrence', 'Status', 'Available at', 'Completed at']}
                          rows={sortedTimepoints.map((stage, index) => {
                            const outOfDateStage = participant?.stagesBehindSchedule?.find(sbs => sbs.stage === stage._id)
                            return [
                              <Box key={index}>
                                {outOfDateStage
                                  ? <Tooltip label={outOfDateStage?.daysLateBy < 1 ? 'This stage is behind schedule' : `This stage is ${outOfDateStage?.daysLateBy} days behind schedule`}><Icon as={FaExclamationTriangle} color='orange' /></Tooltip>
                                  : <Tooltip label='Ready'><Icon as={FaInfoCircle} color='blue.200' /></Tooltip>}
                              </Box>,
                              stage.name,
                              stage.recurrence == null ? <Badge key={index}>Not recurring</Badge> : stage.recurrence + 1,
                              <Box key={index}>
                                {stage.completed
                                  ? <Badge colorPalette='green'><Icon as={FaCheckCircle} /> Completed</Badge>
                                  : (stage.enabled
                                      ? <Badge colorPalette='blue'><Icon as={FaCheckCircle} /> Available</Badge>
                                      : <Badge colorPalette='gray'><Icon as={FaClock} /> Pending</Badge>
                                    )}
                              </Box>,
                              <Stack key={index} direction='row' gap={1} alignItems='center'>
                                <Text>{stage.enableAt ? utils.formatDateTime(stage.enableAt) : <Badge>Unscheduled</Badge>}</Text>
                                {canRescheduleStages && !stage.completed &&
                                  <Button
                                    size='2xs'
                                    variant='ghost'
                                    onClick={() => setScheduleOverrideStage(stage)}
                                  >
                                    <Icon as={FaCalendar} />
                                  </Button>}
                                {canRescheduleStages && !stage.completed && stage.hasScheduleOverride &&
                                  <Button
                                    size='2xs'
                                    variant='ghost'
                                    onClick={() => setResetOverrideStage(stage)}
                                  >
                                    <Icon as={FaSyncAlt} />
                                  </Button>}
                              </Stack>,
                              stage.completedAt ? utils.formatDateTime(stage.completedAt) : '-'
                            ]
                          })}
                        />
                      </Stack>
                    )
                  },
                  {
                    key: 'calendar',
                    title: 'Calendar',
                    content: (
                      <Suspense fallback={<Loading />}>
                        <ParticipantTimelineCalendar
                          trialId={trialId}
                          participant={participant}
                          participantId={participantId}
                          calendarVisible={timelineView === 'calendar'}
                          onStagesRefresh={refreshStages}
                        />
                      </Suspense>
                    )
                  }
                ]}
              />
            </Stack>}
        </>}

      <StageScheduleOverrideModal
        isOpen={scheduleOverrideStage != null}
        onClose={() => setScheduleOverrideStage(null)}
        trialId={trialId}
        participantId={participantId}
        stage={scheduleOverrideStage}
        onSaved={refreshStages}
      />

      <StageScheduleResetPopover
        isOpen={resetOverrideStage != null}
        onClose={() => setResetOverrideStage(null)}
        trialId={trialId}
        participantId={participantId}
        stage={resetOverrideStage}
        onReset={refreshStages}
      />
    </Stack>
  )
}
